import {skipToken} from '@reduxjs/toolkit/query';
import {Parameter} from 'platform/components';
import {useFormatNumber} from 'platform/locale';

import {isNil, isNotNil} from 'ramda';

import {useGetVehicleQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {SnippetProps, getRecordAuditPairResourceLabel} from '@dms/shared';

import {buildArray, composePath, useNavigate} from 'shared/web';

export interface VehicleSnippetResult {
  snippet: SnippetProps | null;
}

const TEST_ID = 'recordAuditDetail-header-vehicleSnippet';

export function useVehicleSnippet(vehicleId: string | null): VehicleSnippetResult {
  const navigate = useNavigate();
  const formatNumber = useFormatNumber();

  const {
    data: vehicle,
    isLoading,
    isFetching,
  } = useGetVehicleQuery(isNotNil(vehicleId) ? {vehicleId} : skipToken);
  // Treat an in-flight refetch (e.g. the id just changed to another record's vehicle) as loading so
  // the snippet never shows the previous vehicle's data during the transition.
  const isVehicleLoading = isLoading || isFetching;

  if (isNil(vehicleId)) {
    return {snippet: null};
  }

  // VIN and licence plate always render with a "No …" placeholder when missing,
  // mirroring the vehicle header (useGetVehicleHeaderInformationParameters); year and
  // mileage are shown only when present. A blank string counts as missing too.
  const vin = vehicle?.vin;
  const registrationPlate = vehicle?.registrationPlate;
  const parameters = buildArray<Parameter>()
    .add(isNotNil(vin) && vin !== '' ? vin : i18n.t('entity.vehicle.notifications.noVIN'))
    .add(
      isNotNil(registrationPlate) && registrationPlate !== ''
        ? registrationPlate
        : i18n.t('entity.vehicle.notifications.noLicencePlate')
    )
    .when(isNotNil(vehicle?.modelYear), vehicle?.modelYear ?? '')
    .when(
      isNotNil(vehicle?.mileage),
      `${formatNumber(vehicle?.mileage ?? 0, 0)} ${i18n.t('general.metric.km')}`
    );

  const snippet: SnippetProps = {
    label: getRecordAuditPairResourceLabel('VEHICLE'),
    title: vehicle?.title ?? (isVehicleLoading ? '…' : i18n.t('general.notifications.notFound')),
    onClick: () => navigate(composePath(vehiclesRoutes.detail, {params: {id: vehicleId}})),
    parameters,
    isLoading: isVehicleLoading,
    'data-testid': TEST_ID,
  };

  return {snippet};
}
